import { BaseOrganizationEndpoints } from "./base";

/** Organisation-level allocation drawn as regions on floor maps. Zones form a tree. */
export type Zone = {
  id: string;
  parent_id: string | null;
  name: string;
  color: string;
  icon: string | null;
  description: string | null;
  sort_order: number;
  created: string;
  updated: string;
};

/** A person who manages a zone (and its sub zones) at one office. */
export type ZoneManagerRef = {
  office_id: string;
  user_id: string;
};

export type ZoneManager = ZoneManagerRef & {
  id: string;
  created: string;
};

/** A zone with the roll-up of every active region tagged with it or a descendant. */
export type ZoneSummary = Zone & {
  office_ids: string[];
  floors: number;
  regions: number;
  own_regions: number;
  desks: number;
  meeting_rooms: number;
  parking: number;
  area_sqm: number | null;
  /** The zone's own managers, per office (not inherited ones). Only sent on
   *  the list and tree to callers allowed to add resources; empty when nobody
   *  manages it. */
  managers?: ZoneManagerRef[];
};

export type ZoneTreeNode = ZoneSummary & {
  children: ZoneTreeNode[];
};

/** The zone's roll-up within one office. */
export type ZoneOfficeSummary = {
  office_id: string;
  floors: number;
  regions: number;
  desks: number;
  meeting_rooms: number;
  parking: number;
  area_sqm: number | null;
};

export type ZoneDetail = ZoneSummary & {
  children: ZoneSummary[];
  regions: Space[];
  offices: ZoneOfficeSummary[];
};

export type SpaceKind = "room" | "open_area" | "corridor";

/** A region drawn on one version of a floor map: room, open area or corridor. */
export type Space = {
  id: string;
  map_id: string;
  floor_id: string;
  office_id: string;
  shape_id: string;
  kind: SpaceKind;
  name: string;
  zone_id: string | null;
  /** The zone the region's desks belong to: its own tag, else the nearest tagged ancestor. */
  effective_zone_id: string | null;
  parent_region_id: string | null;
  depth: number;
  bounds: { x: number; y: number; width: number; height: number };
  geometry: unknown;
  area_px: number;
  area_sqm: number | null;
  area_sqm_override: number | null;
  effective_area_sqm: number | null;
  /** Overlaps another region without nesting; desks in the overlap count toward no space. */
  has_conflict: boolean;
  counts: {
    desks: number;
    meeting_rooms: number;
    parking: number;
    other: number;
  };
  created: string;
  updated: string;
};

export type Placement = {
  space: Space | null;
  zone: Zone | null;
};

/** What keeps a floor's zone numbers from being complete. */
export type FloorHealth = {
  floor_id: string;
  floor_name: string;
  office_id: string;
  has_map: boolean;
  /** The active map knows how many pixels make a metre, so regions have an area. */
  calibrated: boolean;
  desks: number;
  /** Desks counting toward a space; the rest sit in no region or in an unresolved overlap. */
  placed_desks: number;
  regions: number;
  conflicts: number;
  regions_without_area: number;
};

export type ZonesHealth = {
  floors: FloorHealth[];
  zones_not_drawn: Zone[];
};

export type ZoneCreatePayload = {
  name: string;
  color: string;
  icon?: string | null;
  description?: string | null;
  parent_id?: string | null;
  sort_order?: number;
};

/** Nullable fields accept null to clear them; omit a field to leave it unchanged. */
export type ZoneUpdatePayload = Partial<ZoneCreatePayload>;

const BASE_PATH = "/v1/zones";

export class ZonesClient extends BaseOrganizationEndpoints {
  async list(officeId?: string) {
    const params = new URLSearchParams();
    if (officeId) params.set("office_id", officeId);
    const query = params.toString();
    return this.requestWithStatus<ZoneSummary[]>(
      "GET",
      query ? `${BASE_PATH}?${query}` : BASE_PATH
    );
  }

  async tree() {
    return this.requestWithStatus<ZoneTreeNode[]>("GET", `${BASE_PATH}/tree`);
  }

  async health() {
    return this.requestWithStatus<ZonesHealth>("GET", `${BASE_PATH}/health`);
  }

  async get(zoneId: string) {
    return this.requestWithStatus<ZoneDetail>(
      "GET",
      `${BASE_PATH}/${encodeURIComponent(zoneId)}`
    );
  }

  async create(payload: ZoneCreatePayload) {
    return this.requestWithStatus<Zone>("POST", BASE_PATH, payload);
  }

  async update(zoneId: string, payload: ZoneUpdatePayload) {
    return this.requestWithStatus<Zone>(
      "PUT",
      `${BASE_PATH}/${encodeURIComponent(zoneId)}`,
      payload
    );
  }

  async delete(zoneId: string) {
    return this.requestWithStatus<void>(
      "DELETE",
      `${BASE_PATH}/${encodeURIComponent(zoneId)}`
    );
  }

  /** The zone's own managers, per office. Admins and resource managers only. */
  async listManagers(zoneId: string) {
    return this.requestWithStatus<ZoneManager[]>(
      "GET",
      `${BASE_PATH}/${encodeURIComponent(zoneId)}/managers`
    );
  }

  /** Replaces every manager of the zone; an empty list clears them. Admin only. */
  async replaceManagers(zoneId: string, managers: ZoneManagerRef[]) {
    return this.requestWithStatus<ZoneManager[]>(
      "PUT",
      `${BASE_PATH}/${encodeURIComponent(zoneId)}/managers`,
      { managers }
    );
  }

  /** Spaces of a floor's active map, or of another map version when mapId is given. */
  async floorSpaces(floorId: string, mapId?: string) {
    const params = new URLSearchParams();
    if (mapId) params.set("map_id", mapId);
    const query = params.toString();
    const path = `/v1/resources/${encodeURIComponent(floorId)}/spaces`;
    return this.requestWithStatus<Space[]>(
      "GET",
      query ? `${path}?${query}` : path
    );
  }

  async updateSpace(
    floorId: string,
    regionId: string,
    body: { area_sqm_override: number | null }
  ) {
    return this.requestWithStatus<Space>(
      "PUT",
      `/v1/resources/${encodeURIComponent(floorId)}/spaces/${encodeURIComponent(regionId)}`,
      body
    );
  }

  async placement(resourceId: string) {
    return this.requestWithStatus<Placement>(
      "GET",
      `/v1/resources/${encodeURIComponent(resourceId)}/placement`
    );
  }
}
